// File: frontend/src/components/marketing/FAQSection.js
//
// Accessible FAQ (#faq) using MUI Accordion (keyboard support + expanded state
// built in). Answers describe real PesaFii behaviour and may mention sign-in and
// free registration generally — but contain NO password-recovery action, per the
// product rules (recovery lives only inside /login).
//
// Headings: MUI v7's Accordion already wraps the summary in its own <h3>, so the
// question text is a <span>. Making it an h3 as well nests a second heading and
// a screen reader announces every question twice.
//
// Figures: fees come from content/publicPricing.js and the support number from
// config/publicSupport.js, never typed here.

import React from "react";
import {
  Box, Container, Link, Typography, Accordion, AccordionSummary, AccordionDetails,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { PRICING, COLLECTION_FEE_TOTAL_PCT } from "../../content/publicPricing";
import { SUPPORT_PHONE_DISPLAY } from "../../config/publicSupport";

const FAQS = [
  { q: "What is PesaFii?", a: "PesaFii is a hotspot billing and network management platform. It lets you manage routers, create internet packages and vouchers, receive customer payments, monitor users and track revenue from one secure dashboard." },
  { q: "Who is PesaFii designed for?", a: "It's built for hotspot operators, network businesses and growing internet providers who sell Wi-Fi access and need clear visibility over routers, sales and revenue." },
  { q: "How do customers get online?", a: "A customer joins your Wi-Fi, picks a package on your login page and pays with Mobile Money, or enters a voucher code. Once the payment is confirmed, their internet switches on for the time or data they bought. You set the packages, prices and payment options." },
  { q: "Which Mobile Money networks can customers pay with?", a: "MTN Mobile Money and Airtel Money." },
  { q: "Which routers are supported?", a: "PesaFii is built around MikroTik hotspot routers. You add and configure a supported router, then manage its packages, vouchers and reporting from the dashboard." },
  { q: "Do I need technical skills to set up?", a: "You need a MikroTik router with an internet connection. When you add a router, PesaFii gives you a setup script to paste into it and checks the connection for you. If you get stuck, our team can help on WhatsApp or by phone." },
  { q: "Can I sell vouchers?", a: "Yes. You can create voucher batches, print or share the codes, sell them through agents, and track which are available, used or expired." },
  { q: "Can I manage more than one router?", a: "Yes. PesaFii supports multiple routers with reports for each location, and you can focus the dashboard on one router at any time." },
  { q: "Can I brand the Wi-Fi login page?", a: "Yes. You can set your business name, logo, colours, a banner message and your own support number on the page customers see when they join your Wi-Fi." },
  { q: "Can agents or staff use the platform?", a: "Yes. You can add users and agents with role-based permissions so each person only has the access their role needs." },
  { q: "How do I withdraw my earnings?", a: "Customer payments land in your PesaFii wallet. You can withdraw to an MTN or Airtel Mobile Money number. Withdrawals need your withdrawal PIN, and the fee is shown before you confirm." },
  {
    q: "How much does PesaFii cost?",
    a: (
      <>
        Registration is free and there is no monthly subscription. When a customer pays with Mobile Money,
        PesaFii keeps {COLLECTION_FEE_TOTAL_PCT}% of that payment ({PRICING.platformFeePct}% platform fee
        and {PRICING.gatewayFeePct}% payment provider fee) and the rest goes to your wallet. A voucher is
        charged {PRICING.voucherActivationPct}% of its price the first time it is used. Full details are in
        the{" "}
        <Link component={RouterLink} to={PRICING.termsHref}>Terms of Service</Link>.
      </>
    ),
  },
  {
    q: "How do I get support?",
    a: (
      <>
        Chat with us on WhatsApp or call {SUPPORT_PHONE_DISPLAY}. Every way to reach us is on
        the <Link component={RouterLink} to="/contact">Contact page</Link>.
      </>
    ),
  },
];

export default function FAQSection() {
  return (
    <Box component="section" id="faq" sx={{ py: { xs: 6, md: 9 }, scrollMarginTop: 80 }}>
      <Container maxWidth="md" sx={{ minWidth: 0 }}>
        <Box sx={{ textAlign: "center", mb: { xs: 4, md: 5 } }}>
          <Typography sx={{ fontSize: 13, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "primary.main", mb: 1 }}>
            FAQ
          </Typography>
          <Typography component="h2" sx={{ fontSize: "clamp(1.6rem, 3.5vw, 2.4rem)", lineHeight: 1.18, fontWeight: 800, letterSpacing: "-0.02em", color: "text.primary" }}>
            Frequently asked questions
          </Typography>
        </Box>

        <Box>
          {FAQS.map((f, i) => (
            <Accordion
              key={f.q}
              disableGutters
              elevation={0}
              sx={{
                border: "1px solid", borderColor: "divider", borderRadius: "12px !important",
                bgcolor: "background.paper", mb: 1.25,
                "&:before": { display: "none" },
                overflow: "hidden",
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={`faq-content-${i}`}
                id={`faq-header-${i}`}
                sx={{ px: { xs: 2, md: 2.5 }, py: 0.5, "& .MuiAccordionSummary-content": { my: 1.25 } }}
              >
                <Typography component="span" sx={{ display: "block", fontSize: { xs: 15, md: 16 }, fontWeight: 700, color: "text.primary" }}>
                  {f.q}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: { xs: 2, md: 2.5 }, pb: 2, pt: 0 }}>
                <Typography sx={{ fontSize: 14.5, color: "text.secondary", lineHeight: 1.65 }}>{f.a}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      </Container>
    </Box>
  );
}
